import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/*
 * <lainly-tiers>: the product page's offer in the Kaching style (snippets/lainly-product-tiers.liquid).
 * Three cards: Buy 1, Buy 1 Get 2 Free and Buy 2 Get 4 Free.
 *
 * Buy 1 has this piece's dropdowns; Add to cart adds it alone.
 *
 * With data-same-piece (the default now), the bundle cards work like Kaching: this piece once per piece in the
 * bundle, each row ("#1", "#2", ...) with its own dropdowns, and Add to cart adds them all. Without it:
 *
 * The bundle cards are bundle builders over the collection pages' bundle (assets/lainly-bundle.js, the one in the
 * product page's "Pick your pieces" page, sections/lainly-bundle-picker.liquid): choosing one sets the bundle to
 * 3 or 6 pieces and puts this piece first, in the color of its dropdowns. Its gift path shows the bundle's other
 * pieces (photo, name, color, Free or price); an empty stop opens the "Pick your pieces" page, where any piece in
 * the store can be added, and a picked piece's "Remove" takes it out.
 * Once the 3rd piece is picked the page shows "Great choices!" with "Make it 6" (assets/lainly-pick.js); yes
 * makes the bundle 6 pieces and the Buy 2 Get 4 Free card is chosen. The button says what is left to pick (and
 * opens the page) until the bundle is complete, then adds it to the cart. Shopify's "Early Black Friday" discount
 * takes the free pieces off.
 *
 * Picking a color in any row shows its photo in the gallery and names it, with its own number, in the stock line. The
 * "Ships by" date under the button is kept current in the shopper's own time zone.
 */

const FLASH_DURATION = 2000;
const THIS_OUT_KEY = 'lainly-this-out';

function formatNumber(value, decimals, thousands, decimal) {
  const [whole, fraction] = value.toFixed(decimals).split('.');
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
}

/** Formats cents with the shop's money format, e.g. "${{amount}}". */
function formatMoney(cents, format) {
  const value = cents / 100;
  return format
    .replace(/<[^>]*>/g, '')
    .replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return formatNumber(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return formatNumber(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatNumber(value, 0, '.', ',');
        default:
          return formatNumber(value, 2, ',', '.');
      }
    });
}

function readJson(root, selector, fallback) {
  try {
    return JSON.parse(root.querySelector(selector)?.textContent || '');
  } catch {
    return fallback;
  }
}

/** "Rose Gold" → "rose-gold", the key of the color dot photos. */
const handleize = (text) =>
  String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const PLUS_ICON =
  '<svg viewBox="0 0 24 24" width="20" height="20" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>';
const CHEVRON_ICON =
  '<svg viewBox="0 0 24 24" width="18" height="18" focusable="false"><path d="m9 6 6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class LainlyTiers extends HTMLElement {
  /** @type {Array<{id: number, title: string, options: string[], price: number, available: boolean, media: number | null, image: string}>} */
  #variants = [];
  /** @type {Record<string, string>} */
  #swatches = {};
  #busy = false;
  /** What is left to pick, from the bundle ("Pick 2 free pieces"); empty once complete. */
  #pickText = '';
  /** @type {Element | null} */
  #current = null;
  /** The row whose dropdown was changed last: the stock line names its color. @type {Element | null} */
  #stockRow = null;
  #controller = new AbortController();

  /**
   * Whether the shopper took this piece out of the bundle (✕ or "Remove" on "Pick your pieces"), to build it from
   * 0 with other pieces. This visit only: choosing a bundle card again, or adding the bundle, brings it back first.
   */
  get #thisOut() {
    try {
      return sessionStorage.getItem(THIS_OUT_KEY) === this.dataset.url;
    } catch {
      return false;
    }
  }

  set #thisOut(out) {
    try {
      if (out) sessionStorage.setItem(THIS_OUT_KEY, this.dataset.url || '');
      else if (this.#thisOut) sessionStorage.removeItem(THIS_OUT_KEY);
    } catch {
      // Storage blocked: the piece simply comes back first, as before.
    }
  }

  connectedCallback() {
    this.#variants = readJson(this, '[data-tiers-variants]', []);
    this.#swatches = readJson(this, '[data-tiers-swatches]', {});
    const { signal } = this.#controller;

    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    document.addEventListener(
      'lainly:bundle-change',
      (event) => {
        const own = event instanceof CustomEvent && event.target === this.#bundle;
        if (own) this.#pickText = event.detail?.pickText || '';
        if (own && event.detail?.removedUrl && event.detail.removedUrl === this.dataset.url) this.#thisOut = true;
        this.#renderBundle({ otherTab: own && Boolean(event.detail?.otherTab) });
      },
      { signal }
    );

    // Once the bundle is in the cart, the next bundle starts with this piece again.
    document.addEventListener('lainly:bundle-submitted', () => (this.#thisOut = false), { signal });

    for (const row of this.querySelectorAll('[data-tier-row]')) this.#showSwatch(row);
    this.#current = this.#tier;
    customElements.whenDefined('lainly-bundle').then(() => {
      if (this.#isBundle) this.#startBundle();
      this.#renderBundle();
    });
    this.#update();
    this.#renderShipping();
    this.#watchSticky(signal);
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible' && this.#isBundle) this.#renderBundle();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /* ----- Parts ----- */

  get #colorIndex() {
    return Number(this.dataset.colorIndex ?? -1);
  }

  /** The chosen card. */
  get #tier() {
    return this.querySelector('[data-tier-input]:checked')?.closest('[data-tier]') || this.querySelector('[data-tier]');
  }

  /** Pieces in the chosen card: 1, 3 or 6. */
  get #count() {
    return Number(this.#tier?.dataset.tier || 1);
  }

  get #isBundle() {
    return this.#count > 1;
  }

  /** Bundle cards repeat this piece (Kaching style) instead of building a bundle of any pieces. */
  get #samePiece() {
    return this.dataset.samePiece === 'true';
  }

  /** The variant of every piece in a card, one per row, padded with this piece for pieces with nothing to choose. */
  #pieceVariants(tier) {
    const count = Number(tier?.dataset.tier || 1);
    const rows = tier ? [...tier.querySelectorAll('[data-tier-row]')] : [];
    const variants = rows.map((row) => this.#variantFor(row));
    const fallback = variants[0] || this.#thisVariant || (this.#variants.length === 1 ? this.#variants[0] : null);
    while (variants.length < count) variants.push(fallback);
    return variants.slice(0, count);
  }

  /** The bundle builder in the product page's "Pick your pieces" page. */
  get #bundle() {
    const bundle =
      document.querySelector('lainly-pick:not([data-bundle-scope]) lainly-bundle') ||
      document.querySelector('lainly-bundle:not([data-storage="memory"])');
    return bundle && typeof bundle.slots === 'function' ? bundle : null;
  }

  get #picker() {
    const picker = document.querySelector('lainly-pick:not([data-bundle-scope])');
    return picker && typeof picker.open === 'function' ? picker : null;
  }

  /** The variant a row's dropdowns point at, or the only variant of a piece with nothing to choose. */
  #variantFor(row) {
    const selects = row ? [...row.querySelectorAll('select[data-option-index]')] : [];
    if (!selects.length) return this.#variants.length === 1 ? this.#variants[0] : null;
    const values = selects.map((select) => select.value);
    return this.#variants.find((variant) => variant.options.every((value, i) => value === values[i])) || null;
  }

  /** This piece as the chosen card's first row has it. */
  get #thisVariant() {
    return this.#variantFor(this.#tier?.querySelector('[data-tier-row]'));
  }

  /* ----- Events ----- */

  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-tier-input]')) {
      // The new card's first row takes the colors of the last card's first row.
      const from = this.#current?.querySelector('[data-tier-row]');
      const to = this.#tier?.querySelector('[data-tier-row]');
      if (from && to && from !== to) {
        const values = [...from.querySelectorAll('select[data-option-index]')].map((select) => select.value);
        // Same-piece cards start every row in the color just chosen; otherwise only the first.
        const rows = this.#samePiece ? [...(this.#tier?.querySelectorAll('[data-tier-row]') || [])] : [to];
        for (const row of rows) {
          row.querySelectorAll('select[data-option-index]').forEach((select, i) => {
            if (select instanceof HTMLSelectElement && values[i] != null) select.value = values[i];
          });
          this.#showSwatch(row);
        }
      }
      this.#current = this.#tier;
      this.#showError('');
      // Choosing a card puts this piece back first, even if it was taken out.
      this.#thisOut = false;
      // The bundle may already be as the card needs it (no change event): draw the card either way.
      if (this.#isBundle) this.#startBundle();
      this.#renderBundle();
      this.#showPhoto(to);
      return;
    }

    const row = target.closest('[data-tier-row]');
    if (target instanceof HTMLSelectElement && row) {
      this.#stockRow = row;
      this.#showSwatch(row);
      this.#showPhoto(row);
      if (this.#isBundle && !this.#samePiece) this.#putThisFirst();
      this.#update();
    }
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-tier-choose]')) {
      this.#openPicker();
      return;
    }

    // "Remove" takes a picked piece out; its stop becomes "Tap to choose a free piece" again.
    const remove = target.closest('[data-tier-remove]');
    if (remove instanceof HTMLElement) {
      this.#bundle?.remove(Number(remove.dataset.index));
      return;
    }

    if (target.closest('[data-tiers-sticky-claim]')) {
      this.#goToOffer();
      return;
    }

    if (target.closest('[data-tiers-add]')) this.#submit();
  }

  /** The sticky bar's "Claim offer": back up to the offer cards, below the sticky header. */
  #goToOffer() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  /**
   * The sticky bar slides up once ADD TO CART has scrolled up out of view, and away when it comes back. Checked on
   * every scroll (once per frame), not with an IntersectionObserver: a jump from above the button to far below it
   * never crosses the screen, so an observer would miss it. Scrolls are caught on the document, so the desktop
   * page's own scroll box counts too.
   */
  #watchSticky(signal) {
    const sticky = this.querySelector('[data-tiers-sticky]');
    const button = this.querySelector('[data-tiers-add]');
    if (!(sticky instanceof HTMLElement) || !(button instanceof HTMLElement)) return;
    let queued = false;
    const check = () => {
      queued = false;
      const show = button.getBoundingClientRect().bottom < 0;
      if (show === sticky.classList.contains('is-shown')) return;
      sticky.classList.toggle('is-shown', show);
      sticky.inert = !show;
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(check);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true, signal });
    window.addEventListener('resize', onScroll, { passive: true, signal });
    check();
  }


  /* ----- Bundle ----- */

  /** A bundle card was chosen: the bundle takes its size (3 or 6), with this piece first. */
  #startBundle() {
    if (this.#samePiece) return;
    const bundle = this.#bundle;
    if (bundle && bundle.size !== this.#count) bundle.setSize(this.#count);
    this.#putThisFirst();
  }

  /** Puts this piece, in #1's chosen color, first in the bundle. */
  #putThisFirst() {
    if (this.#thisOut) return;
    const bundle = this.#bundle;
    const variant = this.#thisVariant;
    if (!bundle || !variant || typeof bundle.setFirst !== 'function') return;
    const first = bundle.slots()[0]?.item;
    // Already first, with the same photo (a bundle saved before may carry an older one): nothing to do.
    if (first && String(first.variantId) === String(variant.id) && first.image === (variant.image || '')) return;
    bundle.setFirst({
      variantId: String(variant.id),
      title: this.dataset.title || '',
      url: this.dataset.url || '',
      color: variant.title === 'Default Title' ? '' : variant.title,
      price: variant.price,
      image: variant.image || '',
    });
  }

  #openPicker() {
    const picker = this.#picker;
    if (!picker) return;
    this.#putThisFirst();
    picker.open();
  }

  /** The chosen bundle card's gift path as the bundle stands. */
  /** @param {{ otherTab?: boolean }} [options] - otherTab: the bundle was just changed on another tab. */
  #renderBundle({ otherTab = false } = {}) {
    const bundle = this.#bundle;
    if (!bundle || !this.#isBundle || this.#samePiece) {
      this.#update();
      return;
    }

    // "Make it 6" (or a 4th piece) moves the bundle to 6 pieces: the Buy 2 Get 4 Free card is chosen.
    if (bundle.size !== this.#count) {
      const input = this.querySelector(`[data-tier="${bundle.size}"] [data-tier-input]`);
      if (input instanceof HTMLInputElement) {
        const from = this.#tier?.querySelector('[data-tier-row]');
        input.checked = true;
        const to = this.#tier?.querySelector('[data-tier-row]');
        // The new card's first stop keeps the color picked on the last one.
        if (from && to && from !== to) {
          const values = [...from.querySelectorAll('select[data-option-index]')].map((select) => select.value);
          to.querySelectorAll('select[data-option-index]').forEach((select, i) => {
            if (select instanceof HTMLSelectElement && values[i] != null) select.value = values[i];
          });
          this.#showSwatch(to);
        }
        this.#current = this.#tier;
      }
    }

    const slots = bundle.slots();
    // This piece stays first (after the bundle was emptied, or changed on another page). Not when another tab
    // just changed it, nor while this tab is hidden: two product pages open at once would otherwise keep putting
    // their own piece back first, without end. This tab catches up when the shopper comes back to it.
    const first = slots[0]?.item;
    const out = this.#thisOut;
    if ((!first || first.url !== this.dataset.url) && !out && !otherTab && document.visibilityState === 'visible') {
      queueMicrotask(() => this.#putThisFirst());
    }

    const format = bundle.moneyFormat || this.dataset.moneyFormat || '${{amount}}';
    const element = (tag, className, text) => {
      const el = Object.assign(document.createElement(tag), { className });
      if (text != null) el.textContent = text;
      return el;
    };
    const tagFor = (slot) => {
      const tag = element('span', 'lainly-tiers__tag');
      if (slot.free) tag.append(element('span', 'lainly-tiers__free', this.dataset.free || 'Free'));
      else if (slot.price != null) tag.append(formatMoney(Number(slot.price), format));
      return tag;
    };

    // With this piece taken out, its "This piece" row hides and every spot comes from the bundle, from the 1st.
    const thisStop = this.#tier?.querySelector('.lainly-tiers__stop--this');
    if (thisStop instanceof HTMLElement) thisStop.hidden = out;
    const start = out ? 0 : 1;

    const picks = this.#tier?.querySelector('[data-tier-picks]');
    if (picks instanceof HTMLElement) {
      let nextShown = false;
      picks.replaceChildren(
        ...slots.slice(start).map((slot, i) => {
          const index = i + start;

          if (!slot.item) {
            // "Tap to choose a free piece", or "Tap to choose a piece" with its price for the paid one.
            const add = element('button', 'lainly-tiers__stop lainly-tiers__stop--add');
            add.type = 'button';
            add.dataset.tierChoose = '';
            if (!nextShown) {
              add.classList.add('is-next');
              nextShown = true;
            }
            const plus = element('span', 'lainly-tiers__plus');
            plus.innerHTML = PLUS_ICON;
            plus.setAttribute('aria-hidden', 'true');
            const words = element('span', 'lainly-tiers__stop-words');
            const label = slot.free ? this.dataset.choose : this.dataset.choosePaid || this.dataset.choose;
            words.append(element('span', 'lainly-tiers__stop-name', label || ''));
            const chevron = element('span', 'lainly-tiers__chevron');
            chevron.innerHTML = CHEVRON_ICON;
            chevron.setAttribute('aria-hidden', 'true');
            add.append(plus, words, tagFor(slot), chevron);
            return add;
          }

          // A picked piece, with "Remove" under its tag.
          const pick = element('div', 'lainly-tiers__stop lainly-tiers__stop--picked');
          const img = element('img', 'lainly-tiers__photo');
          img.src = (slot.item.image || '').replace(/width=\d+/, 'width=400');
          img.alt = '';
          img.width = 68;
          img.height = 68;
          const words = element('span', 'lainly-tiers__stop-words');
          words.append(element('span', 'lainly-tiers__stop-name', slot.item.title));
          if (slot.item.color) words.append(element('span', 'lainly-tiers__stop-color', slot.item.color));
          const tag = tagFor(slot);
          const remove = element('button', 'lainly-tiers__link', this.dataset.remove || 'Remove');
          remove.type = 'button';
          remove.dataset.tierRemove = '';
          remove.dataset.index = String(index);
          remove.setAttribute('aria-label', `${remove.textContent}: ${slot.item.title}`);
          tag.append(remove);
          pick.append(img, words, tag);
          return pick;
        })
      );
    }

    this.#update();
  }

  /* ----- Display ----- */

  #showSwatch(row) {
    const variant = this.#variantFor(row);
    const thumb = row.querySelector('[data-tier-thumb]');
    if (thumb instanceof HTMLImageElement && variant?.image) thumb.src = variant.image;

    const swatch = row.querySelector('[data-tier-swatch]');
    if (!(swatch instanceof HTMLImageElement)) return;
    const select = row.querySelector(`select[data-option-index="${this.#colorIndex}"]`);
    const src = select instanceof HTMLSelectElement ? this.#swatches[handleize(select.value)] : '';
    swatch.hidden = !src;
    if (src) swatch.src = src;
    row.querySelector('.lainly-tiers__select--color')?.classList.toggle('lainly-tiers__select--no-dot', !src);
  }

  /** Shows the row's color in the gallery. */
  #showPhoto(row) {
    const variant = this.#variantFor(row);
    if (!variant?.media) return;
    this.closest('.lainly-product')
      ?.querySelector('[data-lainly-gallery-root]')
      ?.dispatchEvent(new CustomEvent('lainly:select-media', { detail: { id: variant.media } }));
  }

  /** Prices on every card, the stock line's color and the button. */
  #update() {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const bundle = this.#bundle;

    for (const tier of this.querySelectorAll('[data-tier]')) {
      const count = Number(tier.dataset.tier || 1);
      const variant = this.#variantFor(tier.querySelector('[data-tier-row]')) || this.#thisVariant;
      if (!variant) continue;
      // Before anything is picked: this piece's price for every piece, 2 of every 3 free.
      let paid = variant.price * Math.max(1, count / 3);
      let full = variant.price * count;
      // Same-piece cards price the colors chosen in their rows: the dearest 1 in every 3 is paid.
      if (count > 1 && this.#samePiece) {
        const prices = this.#pieceVariants(tier)
          .map((piece) => Number(piece?.price ?? variant.price))
          .sort((a, b) => b - a);
        full = prices.reduce((sum, price) => sum + price, 0);
        paid = prices.slice(0, Math.max(1, Math.floor(count / 3))).reduce((sum, price) => sum + price, 0);
      }
      // The chosen bundle card prices the pieces actually picked (the 2 cheapest in every 3 are free).
      if (count > 1 && !this.#samePiece && tier === this.#tier && bundle && bundle.size === count) {
        const slots = bundle.slots();
        if (slots.every((slot) => slot.price != null)) {
          paid = slots.reduce((sum, slot) => sum + (slot.free ? 0 : Number(slot.price)), 0);
          full = slots.reduce((sum, slot) => sum + Number(slot.price), 0);
        }
      }
      const priceEl = tier.querySelector('[data-tier-price]');
      const fullEl = tier.querySelector('[data-tier-full]');
      if (priceEl) priceEl.textContent = formatMoney(paid, format);
      if (fullEl) fullEl.textContent = formatMoney(full, format);
      const saveEl = tier.querySelector('[data-tier-save]');
      if (saveEl instanceof HTMLElement && saveEl.dataset.template) {
        saveEl.textContent = saveEl.dataset.template.replace('[amount]', formatMoney(full - paid, format));
      }
    }

    const variant = this.#thisVariant;
    const stock = this.querySelector('[data-tiers-stock]');
    if (stock instanceof HTMLElement && stock.dataset.template) {
      // The color last picked in the chosen card (#1's until another row changes).
      const row = this.#stockRow && this.#tier?.contains(this.#stockRow) ? this.#stockRow : null;
      const picked = (row && this.#variantFor(row)) || variant;
      const color = picked && this.#colorIndex >= 0 ? picked.options[this.#colorIndex] : this.dataset.stockFallback || 'stock';
      stock.innerHTML = stock.dataset.template.replace('[color]', color).replace('[count]', this.#stockCount(color));
    }

    const button = this.querySelector('[data-tiers-add]');
    const label = this.querySelector('[data-tiers-add-label]');
    const available = this.#samePiece
      ? this.#pieceVariants(this.#tier).every((piece) => Boolean(piece?.available))
      : Boolean(variant?.available);
    const missing = this.#isBundle && !this.#samePiece && bundle ? bundle.missing : 0;
    if (button instanceof HTMLButtonElement) button.disabled = !available || this.#busy;
    if (label instanceof HTMLElement && !label.dataset.flashing) {
      if (!available) label.textContent = this.dataset.soldOut || 'Sold out';
      else if (missing > 0) label.textContent = this.#pickText || label.dataset.label || '';
      else label.textContent = label.dataset.label || '';
    }
  }

  /** The stock line's number for a color, from "Gold: 8, Silver: 14, Rose Gold: 24, 8" (the plain number for the rest). */
  #stockCount(color) {
    let fallback = '';
    let match = '';
    for (const pair of (this.dataset.stockCounts || '').split(',')) {
      const [name, count] = pair.split(':').map((part) => part.trim());
      if (count === undefined) fallback = name;
      else if (name.toLowerCase() === String(color).trim().toLowerCase()) match = count;
    }
    return match || fallback;
  }

  #showError(text) {
    const error = this.querySelector('[data-tiers-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = text;
    error.hidden = !text;
  }

  #flash(text) {
    const label = this.querySelector('[data-tiers-add-label]');
    if (!(label instanceof HTMLElement) || !text) return;
    label.dataset.flashing = 'true';
    label.textContent = text;
    clearTimeout(Number(label.dataset.flashTimer));
    label.dataset.flashTimer = String(
      setTimeout(() => {
        delete label.dataset.flashing;
        this.#update();
      }, FLASH_DURATION)
    );
  }

  /**
   * Ships by: orders ship the same or next day, every day but Sunday, so the date is tomorrow (Monday when
   * tomorrow is a Sunday), in the shopper's own time zone. Fast <country> Shipping uses Shopify's guess of
   * the shopper's country.
   */
  #renderShipping() {
    const date = this.querySelector('[data-ship-date]');
    if (date) {
      const ship = new Date();
      ship.setDate(ship.getDate() + 1);
      if (ship.getDay() === 0) ship.setDate(ship.getDate() + 1);
      date.textContent = ship.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    const country = this.querySelector('[data-ship-country]');
    if (!(country instanceof HTMLElement)) return;
    fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' }, signal: this.#controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const code = String(data?.detected_values?.country?.handle || '').toUpperCase();
        if (!/^[A-Z]{2}$/.test(code)) return;
        const label = country.querySelector('[data-ship-code]');
        if (label) label.textContent = code;
        const flag = country.querySelector('[data-ship-flag]');
        if (flag instanceof HTMLImageElement) flag.src = `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
        country.hidden = false;
      })
      .catch(() => {});
  }

  /* ----- Add to cart ----- */

  async #submit() {
    if (this.#busy) return;

    if (this.#isBundle && !this.#samePiece) {
      const bundle = this.#bundle;
      if (!bundle) return;
      // Until the bundle is complete, the button opens the page to pick what is left.
      if (bundle.missing > 0) {
        this.#openPicker();
        return;
      }
      this.#busy = true;
      this.#update();
      try {
        await bundle.addToCart();
        // The bundle empties once it is in the cart.
        if (bundle.slots().every((slot) => !slot.item)) this.#flash(this.dataset.added || '');
      } finally {
        this.#busy = false;
        this.#update();
      }
      return;
    }

    // Every piece of the chosen card (one on Buy 1), the same color joined into one line.
    const pieces = this.#samePiece ? this.#pieceVariants(this.#tier) : [this.#thisVariant];
    if (!pieces.length || !pieces.every((piece) => piece?.available)) return;
    this.#busy = true;
    this.#showError('');
    this.#update();

    /** @type {Array<{id: number, quantity: number}>} */
    const lines = [];
    for (const piece of pieces) {
      const line = lines.find((entry) => entry.id === piece.id);
      if (line) line.quantity += 1;
      else lines.push({ id: piece.id, quantity: 1 });
    }
    const itemCount = pieces.length;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
      }).then((res) => res.json());
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );

      if (response.status) {
        const message = response.description || response.message || 'Something went wrong. Please try again.';
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'lainly-tiers', itemCount },
        });
        this.#showError(message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, source: 'lainly-tiers', itemCount, sections: response.sections },
      });
      this.#flash(this.dataset.added || '');
    } catch (error) {
      deferred.reject(error);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.#busy = false;
      this.#update();
    }
  }
}

if (!customElements.get('lainly-tiers')) customElements.define('lainly-tiers', LainlyTiers);
